import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchMaterialById,
  fetchMaterialSignedUrl,
} from '../../../../features/materials/api';
import type {
  BgmPrompt,
  BgmProvider,
} from '../../../../features/scenarios/api';
import type { Scenario } from '../types';
import { resolveBgmDisplayName, type BgmDisplayNameSource } from '../bgmDisplayName';
import { toBgmDefaultLengthSeconds } from '../bgmDefaultLength';

interface UseBgmEditorParams {
  scenarios: Scenario[];
  activeScenarioId: string | null;
}

// BGM 生成エンジン (ElevenLabs / Gemini クリップ / Gemini フル曲) の最後の選択を保持する
// localStorage キー。シナリオ横断のグローバル設定として扱う。
const BGM_PROVIDER_KEY = 'bgm_provider_last';

function readStoredBgmProvider(): BgmProvider {
  try {
    const v = localStorage.getItem(BGM_PROVIDER_KEY);
    if (v === 'elevenlabs' || v === 'lyria-pro') return v;
    // Gemini のクリップ (lyria) は尺を指定できず常に約 30 秒になるため選択肢から外した (2026-09-29)。保存値はフル曲へ寄せる
    if (v === 'lyria') return 'lyria-pro';
  } catch { /* ignore */ }
  return 'elevenlabs';
}

// 歌の有無トグル (全プロバイダ・全シナリオ横断の単一値)。既定はボーカルなし (false)。
const BGM_VOCAL_KEY = 'bgm_vocal';

// BGM プロンプトの入力方法 (標準 = 従来のプロンプト編集 / テンプレート = 非開示テンプレートを
// 名前で選ぶ / 歌詞 = シーンの narration を歌詞として Lyria Pro に歌わせる。
// docs/features/scenario-song-lyrics-bgm-plan.md §5.5)。
// プロバイダやボーカルと違い localStorage には保持しない (常に「標準」または歌モノの
// ソフトロックで「歌詞」から始まる)。
export type BgmPromptMode = 'standard' | 'template' | 'lyrics';

function readStoredBgmVocal(): boolean {
  try {
    return localStorage.getItem(BGM_VOCAL_KEY) === 'true';
  } catch { /* ignore */ }
  return false;
}

/** シナリオの generation_metadata.bgm_mode==='lyrics' か (歌モノ・シナリオ既定・層 A)。 */
export function isLyricsModeScenario(scenario: Scenario | undefined | null): boolean {
  const meta = scenario?.generation_metadata as Record<string, unknown> | undefined;
  return meta?.bgm_mode === 'lyrics';
}

/**
 * 歌モノで、歌 (BGM) がまだ採用されていない状態 (計画 md §5.5.1)。
 * 動画生成ゲートと動画カード自動分割の後回しで共有する判定。
 * 「採用しない」(bgm_excluded) や通常 BGM のアップロードでも解除される (ユーザーを閉じ込めない)。
 */
export function isLyricsBgmPending(scenario: Scenario | undefined | null): boolean {
  if (!isLyricsModeScenario(scenario)) return false;
  return !scenario?.bgm_material_id && !scenario?.bgm_excluded;
}

/**
 * 素材の generation_metadata.is_vocal_lead (層 B・§3.6)。歌詞モードで生成した BGM にのみ
 * true が付く。旧素材 (フィールド無し) は false 扱い。
 */
export function resolveMaterialIsVocalLead(material: { generation_metadata?: Record<string, unknown> | null } | null | undefined): boolean {
  return material?.generation_metadata?.is_vocal_lead === true;
}

/**
 * シーン毎の実効尺 (秒) のリストを返す。
 * activeScenarioDurationSeconds の合計計算で使う
 * (バックエンド bgm_generator._scene_durations と同趣旨のフロント版)。
 */
export function getSceneDurations(scenario: Scenario | undefined): number[] {
  const scenes = scenario?.scenes;
  if (!scenes || scenes.length === 0) return [];
  const isStoryboard = scenario?.frame_mode === 'storyboard';
  return scenes.map((s) => {
    if (s.brand_cut_position) {
      const boundVideoDur = s.video_material_id
        ? (s.video_duration_seconds
            || ((s.generation_metadata as Record<string, unknown> | undefined)?.duration_seconds as number | undefined)
            || 0)
        : 0;
      return boundVideoDur > 0 ? boundVideoDur : (s.brand_cut_duration_seconds ?? 3);
    }
    return isStoryboard
      ? (s.storyboard_duration_seconds ?? 2)
      : (s.video_duration_seconds ?? 4);
  });
}

interface UseBgmEditorReturn {
  bgmGeneratingScenarios: Record<string, boolean>;
  startBgmGeneration: (scenarioId: string) => boolean;
  finishBgmGeneration: (scenarioId: string) => void;
  isBgmScenarioActive: (scenarioId: string) => boolean;
  bgmUploading: boolean;
  setBgmUploading: React.Dispatch<React.SetStateAction<boolean>>;
  bgmAudioUrl: string | null;
  setBgmAudioUrl: React.Dispatch<React.SetStateAction<string | null>>;
  bgmFileName: string | null;
  setBgmFileName: React.Dispatch<React.SetStateAction<string | null>>;
  bgmFileInputRef: React.RefObject<HTMLInputElement | null>;
  bgmExpanded: boolean;
  setBgmExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  bgmPlaying: boolean;
  setBgmPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  // 生成完了後まだ一度も再生していない状態 (再生ボタンのパルスリング表示用)。動画/ナレーションと同趣旨。
  bgmUnplayed: boolean;
  setBgmUnplayed: React.Dispatch<React.SetStateAction<boolean>>;
  bgmAudioRef: React.MutableRefObject<HTMLAudioElement | null>;
  bgmSongTitle: string | null;
  setBgmSongTitle: React.Dispatch<React.SetStateAction<string | null>>;
  bgmDuration: number | null;
  setBgmDuration: React.Dispatch<React.SetStateAction<number | null>>;
  bgmLengthSeconds: number | null;
  setBgmLengthSeconds: React.Dispatch<React.SetStateAction<number | null>>;
  bgmPromptDraft: BgmPrompt | null;
  setBgmPromptDraft: React.Dispatch<React.SetStateAction<BgmPrompt | null>>;
  bgmPromptEditing: boolean;
  setBgmPromptEditing: React.Dispatch<React.SetStateAction<boolean>>;
  bgmPromptFetching: boolean;
  setBgmPromptFetching: React.Dispatch<React.SetStateAction<boolean>>;
  bgmProvider: BgmProvider;
  setBgmProvider: React.Dispatch<React.SetStateAction<BgmProvider>>;
  bgmUseStoryboardRef: boolean;
  setBgmUseStoryboardRef: React.Dispatch<React.SetStateAction<boolean>>;
  bgmVocal: boolean;
  setBgmVocal: React.Dispatch<React.SetStateAction<boolean>>;
  /** プロンプトの入力方法 (標準 / テンプレート)。セッション内 state のみ (localStorage 非保持) */
  bgmPromptMode: BgmPromptMode;
  setBgmPromptMode: React.Dispatch<React.SetStateAction<BgmPromptMode>>;
  /** 入力方法タブの手動切替。歌詞モードは Lyria Pro・ボーカルあり固定だが localStorage には書かず、
   *  歌詞モードから抜けたら provider / vocal を localStorage の実値に戻す
   *  (一度歌詞を選ぶと全シナリオでボーカルあり / Lyria Pro のまま残るのを防ぐ)。 */
  selectBgmPromptMode: (mode: BgmPromptMode) => void;
  /** 選択中の BGM テンプレート key (属性ツリー option key)。セッション内 state のみ (localStorage 非保持) */
  bgmTemplateKey: string | null;
  setBgmTemplateKey: React.Dispatch<React.SetStateAction<string | null>>;
  bgmUploadMode: boolean;
  setBgmUploadMode: React.Dispatch<React.SetStateAction<boolean>>;
  /** 入力方法タブが「フリー楽曲」か (docs/features/free-bgm-catalog-plan.md)。true のとき生成 UI を
   *  隠し、運営 / パートナー提供の楽曲一覧を出す。アップロードと同様シナリオ切替で false に戻す。 */
  bgmFreeTrackMode: boolean;
  setBgmFreeTrackMode: React.Dispatch<React.SetStateAction<boolean>>;
  /** 採用中 BGM 素材の複製元カタログ項目 id (generation_metadata.audio_catalog_item_id)。
   *  フリー楽曲から選んだものでなければ null。一覧の「選択中」表示に使う。 */
  bgmCatalogItemId: string | null;
  setBgmCatalogItemId: React.Dispatch<React.SetStateAction<string | null>>;
  /** アクティブシナリオが歌モノ (bgm_mode==='lyrics') で、まだセッション内で解除していないか
   *  (ソフトロック中か)。true の間 BgmSection は入力方法/モード/ボーカルを固定表示にする
   *  (docs/features/scenario-song-lyrics-bgm-plan.md §6.1)。 */
  bgmLyricsLocked: boolean;
  /** ロックをセッション内だけ解除する (「歌詞モードを解除して通常の BGM を作る」)。
   *  localStorage には書かず、シナリオを切り替えて戻ると再びロックされる。 */
  releaseBgmLyricsLock: () => void;
  /** アクティブシナリオで採用中の BGM 素材が is_vocal_lead (ボーカル主体) か (層 B・§3.6)。
   *  bgm_excluded=true のときは false 扱い。ナレーション生成ボタンの無効化に使う。 */
  bgmIsVocalLead: boolean;
  setBgmIsVocalLead: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useBgmEditor({
  scenarios,
  activeScenarioId,
}: UseBgmEditorParams): UseBgmEditorReturn {
  const [bgmGeneratingScenarios, setBgmGeneratingScenarios] = useState<Record<string, boolean>>({});
  const generatingScenarioIdsRef = useRef(new Set<string>());
  const startBgmGeneration = useCallback((scenarioId: string) => {
    // 再描画前の連打も防ぐ。同じシナリオだけをロックし、別シナリオは並行生成できる。
    if (generatingScenarioIdsRef.current.has(scenarioId)) return false;
    generatingScenarioIdsRef.current.add(scenarioId);
    setBgmGeneratingScenarios((prev) => ({ ...prev, [scenarioId]: true }));
    return true;
  }, []);
  const finishBgmGeneration = useCallback((scenarioId: string) => {
    generatingScenarioIdsRef.current.delete(scenarioId);
    setBgmGeneratingScenarios((prev) => {
      const next = { ...prev };
      delete next[scenarioId];
      return next;
    });
  }, []);
  const [bgmUploading, setBgmUploading] = useState(false);
  const [bgmAudioUrl, setBgmAudioUrl] = useState<string | null>(null);
  const [bgmFileName, setBgmFileName] = useState<string | null>(null);
  const bgmFileInputRef = useRef<HTMLInputElement>(null);
  const [bgmExpanded, setBgmExpanded] = useState(false);
  const [bgmPlaying, setBgmPlaying] = useState(false);
  // 生成完了後まだ再生していないか。生成成功で true、再生開始で false、シナリオ切替でリセット。
  const [bgmUnplayed, setBgmUnplayed] = useState(false);
  const bgmAudioRef = useRef<HTMLAudioElement | null>(null);
  const [bgmSongTitle, setBgmSongTitle] = useState<string | null>(null);
  const [bgmDuration, setBgmDuration] = useState<number | null>(null);
  // BGM 生成時のターゲット長さ (秒)。null のときは scenario.generation_metadata.duration が使われる。
  const [bgmLengthSeconds, setBgmLengthSeconds] = useState<number | null>(null);

  const [bgmPromptDraft, setBgmPromptDraft] = useState<BgmPrompt | null>(null);
  const [bgmPromptEditing, setBgmPromptEditing] = useState(false);
  const [bgmPromptFetching, setBgmPromptFetching] = useState(false);
  // BGM 生成プロバイダ (ElevenLabs / Lyria Pro。Lyria Clip は 2026-09-29 に選択肢から外した)。最後の選択を localStorage に保持。
  const [bgmProvider, setBgmProviderState] = useState<BgmProvider>(readStoredBgmProvider);
  const setBgmProvider = useCallback<React.Dispatch<React.SetStateAction<BgmProvider>>>((action) => {
    setBgmProviderState((prev) => {
      const next = typeof action === 'function'
        ? (action as (p: BgmProvider) => BgmProvider)(prev)
        : action;
      try { localStorage.setItem(BGM_PROVIDER_KEY, next); } catch { /* ignore */ }
      return next;
    });
  }, []);
  // ストーリーボードを参照画像として使うか (Lyria 系のみ)。デフォルト ON。
  const [bgmUseStoryboardRef, setBgmUseStoryboardRef] = useState(true);
  // 入力方法タブが「アップロード」かどうか。true のとき生成 UI を隠しアップロード領域を出す。
  // シナリオ切替で false (生成) に戻す。
  const [bgmUploadMode, setBgmUploadMode] = useState(false);
  // 入力方法タブが「フリー楽曲」かどうか。アップロードと排他 (BgmSection のタブ onChange が両方を更新する)。
  const [bgmFreeTrackMode, setBgmFreeTrackMode] = useState(false);
  // 採用中 BGM 素材の複製元カタログ項目 id。素材の generation_metadata から復元する。
  const [bgmCatalogItemId, setBgmCatalogItemId] = useState<string | null>(null);
  // 歌の有無 (全プロバイダ横断)。true=ボーカルあり。既定なし。最後の選択を localStorage に保持。
  const [bgmVocal, setBgmVocalState] = useState<boolean>(readStoredBgmVocal);
  // プロンプトの入力方法 (標準 / テンプレート) と選択中テンプレート key。
  // localStorage には保持せず、画面を開くたびに「標準」/未選択から始める。
  const [bgmPromptMode, setBgmPromptMode] = useState<BgmPromptMode>('standard');
  const [bgmTemplateKey, setBgmTemplateKey] = useState<string | null>(null);
  const setBgmVocal = useCallback<React.Dispatch<React.SetStateAction<boolean>>>((action) => {
    setBgmVocalState((prev) => {
      const next = typeof action === 'function'
        ? (action as (p: boolean) => boolean)(prev)
        : action;
      try { localStorage.setItem(BGM_VOCAL_KEY, next ? 'true' : 'false'); } catch { /* ignore */ }
      return next;
    });
  }, []);
  // 歌モノ (bgm_mode==='lyrics') シナリオのソフトロックをセッション内だけ解除したか
  // (docs/features/scenario-song-lyrics-bgm-plan.md §6.1)。シナリオを切り替えると
  // 再びロックされる (保存しない)。
  const [bgmLyricsLockReleased, setBgmLyricsLockReleased] = useState(false);
  // アクティブシナリオで採用中の BGM 素材が is_vocal_lead (ボーカル主体・層 B) か。
  // ナレーション生成ボタンの無効化 (§3.6) に使う。
  const [bgmIsVocalLead, setBgmIsVocalLead] = useState(false);
  // 直前のシナリオ ID。リセット effect は bgm_material_id 変化でも再発火するため、
  // 「シナリオが実際に切り替わったか」を判定して未再生フラグの誤リセットを防ぐ。
  const prevScenarioIdRef = useRef<string | null | undefined>(undefined);
  // 歌モノソフトロックを適用済みのシナリオ id。シェルフロー (ランダム生成) では
  // activeScenarioId がプレースホルダー (generation_metadata に bgm_mode が無い) の時点で
  // 切り替わり、本文差し替え後は activeScenarioId が変わらないため、切替 effect だけでは
  // ロックが掛からない (2026-09-15 実機報告: 歌モノなのに「歌詞」が既定選択されない)。
  // 下の追随 effect が「同一シナリオのまま bgm_mode==='lyrics' になった」ときにも掛ける。
  const lyricsLockAppliedIdRef = useRef<string | null | undefined>(null);

  // アクティブシナリオのBGMファイル名を初期取得
  const activeScenario = useMemo(
    () => scenarios.find((s) => s.id === (activeScenarioId ?? scenarios[0]?.id)),
    [scenarios, activeScenarioId],
  );
  const activeBgmMaterialId = activeScenario?.bgm_material_id;
  const currentScenarioIdRef = useRef(activeScenario?.id);
  useEffect(() => {
    currentScenarioIdRef.current = activeScenario?.id;
    return () => { currentScenarioIdRef.current = undefined; };
  }, [activeScenario?.id]);
  // 生成開始時のコールバックからも、完了時に表示中のシナリオを確認できるようにする。
  const isBgmScenarioActive = useCallback((scenarioId: string) => currentScenarioIdRef.current === scenarioId, []);
  // BGM 長さ入力のデフォルト値 (秒)。BgmSection / backend の BGM 生成フォールバックと揃える:
  //   1. シナリオの想定尺 ("15秒" 等)
  //   2. 想定尺が無いとき (ストーリーボードモードは想定尺を使わない) はシーン尺の合計
  //      (ストーリーボード=コマ尺 storyboard_duration_seconds 既定 2 秒 / それ以外=video_duration_seconds 既定 4 秒)
  // BGM は本編とは違い出力全体に被せるため、ブランドカット (タイトルカード/エンドカード) も
  // 含めた総尺をデフォルトにする。ブランドカットは実効尺 (紐付け動画の実尺 → brand_cut_duration_seconds
  // 既定 3 秒) で数え、SceneItem の getSceneEffectiveDuration と揃える。
  // いずれも BGM_DEFAULT_MIN_SECONDS (30秒) 未満なら切り上げる (エディタでトリムできるため)。
  const activeScenarioDurationSeconds = useMemo(() => {
    const durationStr = (activeScenario?.generation_metadata as Record<string, unknown> | undefined)?.duration;
    if (typeof durationStr === 'string') {
      const m = /(\d+)/.exec(durationStr);
      if (m) {
        const n = Number(m[1]);
        if (n > 0) return toBgmDefaultLengthSeconds(n);
      }
    }
    const scenes = activeScenario?.scenes;
    if (scenes && scenes.length > 0) {
      const isStoryboard = activeScenario?.frame_mode === 'storyboard';
      const total = scenes.reduce((acc, s) => {
        let per: number;
        if (s.brand_cut_position) {
          const boundVideoDur = s.video_material_id
            ? (s.video_duration_seconds
                || ((s.generation_metadata as Record<string, unknown> | undefined)?.duration_seconds as number | undefined)
                || 0)
            : 0;
          per = boundVideoDur > 0 ? boundVideoDur : (s.brand_cut_duration_seconds ?? 3);
        } else {
          per = isStoryboard
            ? (s.storyboard_duration_seconds ?? 2)
            : (s.video_duration_seconds ?? 4);
        }
        return acc + per;
      }, 0);
      if (total > 0) return toBgmDefaultLengthSeconds(total);
    }
    return null;
  }, [activeScenario]);

  useEffect(() => {
    let cancelled = false;
    // この effect は bgm_material_id 変化 (生成完了直後など) でも再発火する。
    // 未再生パルスのフラグは「シナリオが実際に切り替わった」ときだけリセットし、
    // 同一シナリオ内での生成完了直後に消えてしまわないようにする。
    const nextScenarioId = activeScenarioId ?? scenarios[0]?.id;
    const scenarioChanged = prevScenarioIdRef.current !== nextScenarioId;
    prevScenarioIdRef.current = nextScenarioId;

    // シナリオ切替時にBGM状態をリセット
    setBgmAudioUrl(null);
    setBgmFileName(null);
    setBgmSongTitle(null);
    setBgmDuration(null);
    setBgmLengthSeconds(activeScenarioDurationSeconds);
    setBgmPlaying(false);
    if (scenarioChanged) setBgmUnplayed(false);
    setBgmPromptEditing(false);
    // プロバイダ / ボーカル有無はシナリオ横断で最後の選択を維持する (localStorage 由来)。
    // 「シナリオが実際に切り替わったとき」だけ再同期する。この effect は同一シナリオ内の
    // bgm_material_id 変化 (生成完了直後など) でも再発火するため、無条件に読み直すと
    // 歌モノロック中に BGM を生成した直後に provider/vocal が localStorage の値へ
    // ドリフトしてしまう (レビュー指摘)。歌モノのソフトロック (下記) はこの直後に
    // 上書きすることがあるが、localStorage へは書き込まない (生の setState を使う)。
    if (scenarioChanged) {
      setBgmProviderState(readStoredBgmProvider());
      setBgmVocalState(readStoredBgmVocal());
    }
    // 入力方法は「シナリオが切り替わったとき」だけ生成タブに戻す。
    // (アップロード完了で bgm_material_id が変わっても effect は再発火するため、
    //  ここで無条件にリセットするとアップロード直後に Gemini 画面へ飛んでしまう)
    if (scenarioChanged) {
      setBgmUploadMode(false);
      setBgmFreeTrackMode(false);
    }
    // プロンプト編集ドラフトは scenario id キーの localStorage から復元
    const currentScenarioId = activeScenarioId ?? scenarios[0]?.id;
    try {
      const raw = currentScenarioId ? localStorage.getItem(`bgm_prompt_override_${currentScenarioId}`) : null;
      setBgmPromptDraft(raw ? (JSON.parse(raw) as BgmPrompt) : null);
    } catch {
      setBgmPromptDraft(null);
    }
    // ストーリーボード参照は「シナリオ切替時のみ」既定 (ON) に戻す。
    // (生成完了で bgm_material_id が変わっても effect は再発火するため、無条件に
    //  戻すと OFF にしていても生成後に ON へ戻ってしまう)
    if (scenarioChanged) setBgmUseStoryboardRef(true);
    if (bgmAudioRef.current) {
      bgmAudioRef.current.pause();
      bgmAudioRef.current = null;
    }

    // 歌モノシナリオのソフトロック (§6.1)。シナリオが切り替わったときだけ適用/解除する
    // (同一シナリオ内での bgm_material_id 変化では触らない)。解除セッションもここでリセット。
    if (scenarioChanged) {
      setBgmLyricsLockReleased(false);
      const nextScenario = scenarios.find((s) => s.id === nextScenarioId);
      if (isLyricsModeScenario(nextScenario)) {
        // localStorage には書かない (生の setState を直接呼ぶ)。
        setBgmPromptMode('lyrics');
        setBgmTemplateKey(null);
        setBgmProviderState('lyria-pro');
        setBgmVocalState(true);
        lyricsLockAppliedIdRef.current = nextScenarioId;
      } else {
        setBgmPromptMode('standard');
        setBgmTemplateKey(null);
        lyricsLockAppliedIdRef.current = null;
      }
    }

    if (activeBgmMaterialId) {
      fetchMaterialById(activeBgmMaterialId)
        .then((m) => {
          if (!cancelled && m) {
            // 表示名は name > song_title > アップロード元ファイル名 > 「BGM (自動生成)」 >
            // storage_path のファイル名 (bgmDisplayName.ts)。展開時 / 履歴切替と同じ規則。
            const displayName = resolveBgmDisplayName(m as BgmDisplayNameSource);
            if (displayName) setBgmFileName(displayName);
            const meta = (m as { generation_metadata?: { song_title?: string } }).generation_metadata;
            if (meta?.song_title) setBgmSongTitle(meta.song_title);
            const dur = (m as { duration_seconds?: number }).duration_seconds;
            if (dur) setBgmDuration(dur);
            // 層 B (§3.6): 採用中 BGM の is_vocal_lead。bgm_excluded=true なら false 扱い。
            const excluded = !!scenarios.find((s) => s.id === nextScenarioId)?.bgm_excluded;
            setBgmIsVocalLead(!excluded && resolveMaterialIsVocalLead(m as { generation_metadata?: Record<string, unknown> | null }));
            // フリー楽曲から複製した素材なら複製元のカタログ項目 id を持つ (一覧の「選択中」表示用)
            const catalogId = (m as { generation_metadata?: { audio_catalog_item_id?: unknown } }).generation_metadata?.audio_catalog_item_id;
            setBgmCatalogItemId(typeof catalogId === 'string' && catalogId ? catalogId : null);
          }
        })
        .catch(() => {});
      // 再生用signed URLを取得
      fetchMaterialSignedUrl(activeBgmMaterialId, true)
        .catch(() => fetchMaterialSignedUrl(activeBgmMaterialId, false))
        .then((url) => {
          if (!cancelled && url) setBgmAudioUrl(url);
        })
        .catch(() => {});
    } else {
      setBgmIsVocalLead(false);
      setBgmCatalogItemId(null);
    }
    return () => { cancelled = true; };
    // activeBgmMaterialId ではなく activeScenarioId で切替を検知する
    // (シナリオ切替 + 未設定 → 未設定 のとき effect が発火しないのを防ぐ)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScenarioId, activeScenario?.id, activeBgmMaterialId, activeScenarioDurationSeconds]);

  const activeIsLyricsScenario = isLyricsModeScenario(activeScenario);
  const bgmLyricsLocked = activeIsLyricsScenario && !bgmLyricsLockReleased;

  // 歌モノを開いたときは展開する。手動で閉じた後は、同じシナリオの更新で開き直さない。
  // 本文の到着後に歌モノと判明したシェルにも追随する。
  useEffect(() => {
    setBgmExpanded(activeIsLyricsScenario);
  }, [activeScenario?.id, activeIsLyricsScenario]);

  // 同一シナリオのまま後から bgm_mode==='lyrics' が届いた場合 (シェルの本文差し替え) の追随。
  // 切替 effect で適用済み (lyricsLockAppliedIdRef) なら何もしない。解除中も掛け直さない。
  useEffect(() => {
    const currentId = activeScenarioId ?? scenarios[0]?.id;
    if (!activeIsLyricsScenario || bgmLyricsLockReleased) return;
    if (lyricsLockAppliedIdRef.current === currentId) return;
    lyricsLockAppliedIdRef.current = currentId;
    setBgmPromptMode('lyrics');
    setBgmTemplateKey(null);
    setBgmProviderState('lyria-pro');
    setBgmVocalState(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIsLyricsScenario, activeScenarioId, bgmLyricsLockReleased]);

  /**
   * 「歌詞モードを解除して通常の BGM を作る」。セッション内だけ解除する (localStorage
   * には書かない・シナリオを切り替えて戻ると再びロックされる)。解除後は通常の UI に戻し、
   * プロバイダ / ボーカルは localStorage の実値に戻す (§6.1)。
   */
  const releaseBgmLyricsLock = useCallback(() => {
    setBgmLyricsLockReleased(true);
    setBgmPromptMode('standard');
    setBgmTemplateKey(null);
    setBgmProviderState(readStoredBgmProvider());
    setBgmVocalState(readStoredBgmVocal());
  }, []);

  const selectBgmPromptMode = useCallback((mode: BgmPromptMode) => {
    setBgmPromptMode(mode);
    if (mode === 'lyrics') {
      // 歌モノソフトロックと同じく生の setState (localStorage へは書かない)。
      setBgmProviderState('lyria-pro');
      setBgmVocalState(true);
    } else {
      setBgmProviderState(readStoredBgmProvider());
      setBgmVocalState(readStoredBgmVocal());
    }
  }, []);

  return {
    bgmGeneratingScenarios,
    startBgmGeneration,
    finishBgmGeneration,
    isBgmScenarioActive,
    bgmUploading,
    setBgmUploading,
    bgmAudioUrl,
    setBgmAudioUrl,
    bgmFileName,
    setBgmFileName,
    bgmFileInputRef,
    bgmExpanded,
    setBgmExpanded,
    bgmPlaying,
    setBgmPlaying,
    bgmUnplayed,
    setBgmUnplayed,
    bgmAudioRef,
    bgmSongTitle,
    setBgmSongTitle,
    bgmDuration,
    setBgmDuration,
    bgmLengthSeconds,
    setBgmLengthSeconds,
    bgmPromptDraft,
    setBgmPromptDraft,
    bgmPromptEditing,
    setBgmPromptEditing,
    bgmPromptFetching,
    setBgmPromptFetching,
    bgmProvider,
    setBgmProvider,
    bgmUseStoryboardRef,
    setBgmUseStoryboardRef,
    bgmVocal,
    setBgmVocal,
    bgmPromptMode,
    setBgmPromptMode,
    selectBgmPromptMode,
    bgmTemplateKey,
    setBgmTemplateKey,
    bgmUploadMode,
    setBgmUploadMode,
    bgmFreeTrackMode,
    setBgmFreeTrackMode,
    bgmCatalogItemId,
    setBgmCatalogItemId,
    bgmLyricsLocked,
    releaseBgmLyricsLock,
    bgmIsVocalLead,
    setBgmIsVocalLead,
  };
}

export type { UseBgmEditorReturn };
